import {IntRange} from '../utils/IntRange';
import {IntSequence} from '../utils/IntSequence';
import {VanillaReader} from '../VanillaReader/VanillaReader';
import {
    IVanillaReaderShelfItemData,
    IVanillaReaderSyncMediaOptions,
    IVanillaReaderUiColorTheme,
    IVanillaReaderUserProfileData,
    IVanillaTtsServiceProvider,
    VanillaVoidCallback,
} from '../VanillaReader/VanillaReaderModel';
import {VanillaReaderBookshelf} from '../VanillaReaderBookshelf/VanillaReaderBookshelf';
import {VanillaReaderShelfItem} from '../VanillaReaderBookshelf/VanillaReaderShelfItem';
import {VanillaReaderEventBus} from '../VanillaReaderEventBus/VanillaReaderEventBus';
import {VanillaReaderUI} from '../VanillaReaderUI/VanillaReaderUI';
import {IVanillaReaderAppMainInitialOptionsData} from './IVanillaReaderAppMainInitialOptionsData';
import {VanillaReaderAppStorage} from './VanillaReaderAppStorage';
import {VanillaReaderAppUserProfile} from './VanillaReaderAppUserProfile';
import {VanillaReaderLocalStorageKeys} from "../VanillaReaderDataStore/VanillaReaderStoreModel";
import {VanillaReaderUiActiveOptionsPresetData,} from "../VanillaReaderUI/VanillaReaderUiModel";
import {VanillaReaderAppAssets} from "./VanillaReaderAppAssets";
import {EpubCfiParser} from "@colibrio/colibrio-reader-framework/colibrio-core-selector-epubcfi";
import {TreeNodeWalker} from "@colibrio/colibrio-reader-framework/colibrio-core-base";
import {VanillaReaderAPI} from "../VanillaReaderAPI/VanillaReaderAPI";
import {ObjectUtils} from "../utils/base/ObjectUtils/ObjectUtils";
import { MasterPlanPostMessageHandler } from '../MasterPlanUI/MasterPlanPostMessageHandler';
import { COLIBRIO_VERSION } from '../version';

/**
 *
 * # VanillaReaderAppMain
 *
 * ## RESPONSIBILITIES
 *
 * This class has all the "bootstrapping" logic to start the application. It creates the `VanillaReaderUI`,
 * `VanillaReader`, the `VanillaReaderAPI` as well as the simple `VanillaReaderBookshelf` module.
 *
 * ## RELATED TYPES
 *
 * - VanillaReader
 * - VanillaReaderUI
 * - VanillaReaderAPI
 * - VanillaReaderBookshelf
 *
 * */
export class VanillaReaderAppMain {
    private readonly _vanillaReaderBookshelf: VanillaReaderBookshelf;
    private readonly _vanillaReaderAppStorage: VanillaReaderAppStorage;
    private _vanillaReader: VanillaReader | undefined;
    private _vanillaReaderUi: VanillaReaderUI | undefined;
    private _vanillaReaderAPI: VanillaReaderAPI | undefined;
    private _isOffline: boolean = true;
    private _onAppLoadedCallback: VanillaVoidCallback | undefined;
    private _onVanillaReaderInstanceCreatedCallback: VanillaVoidCallback | undefined;
    private _onVanillaReaderUiInstanceCreatedCallback: VanillaVoidCallback | undefined;

    private _activeAppUiTheme: IVanillaReaderUiColorTheme | undefined;

    constructor(
        private _applicationViewElement: HTMLElement,
        private _licenseKey: string,
        private _initialOptions: IVanillaReaderAppMainInitialOptionsData,
    ) {
        this._vanillaReaderAppStorage = new VanillaReaderAppStorage();
        this._vanillaReaderBookshelf = new VanillaReaderBookshelf(this._vanillaReaderAppStorage);
        this._vanillaReaderBookshelf.onBookshelfUpdated(VanillaReaderAppMain._onBookshelfUpdated);
        this._vanillaReaderBookshelf.onShelfItemDeleted(VanillaReaderAppMain._onBookshelfItemDeleted);
    }

    public static get colibrioFrameworkVersion(): string {
        return COLIBRIO_VERSION;
    }

    public static get appVersion(): string {
        return COLIBRIO_VERSION;
    }

    public get isOffline(): boolean {
        return this._isOffline;
    }

    public get useDeviceStorage(): boolean {
        return this._initialOptions.useDeviceStorage;
    }

    public get activeAppUiTheme(): IVanillaReaderUiColorTheme | undefined {
        return this._activeAppUiTheme;
    }

    async _asyncSetup(): Promise<void> {

        await this._vanillaReaderAppStorage.initialize();

        let defaultPalette: IVanillaReaderUiColorTheme = VanillaReaderAppAssets.ColorThemes.find(palette => palette.name === 'Default')!;
        let activePaletteString = localStorage.getItem(VanillaReaderLocalStorageKeys.VanillaReaderActiveColorPalette);
        let activePaletteData: IVanillaReaderUiColorTheme = activePaletteString ?
            JSON.parse(activePaletteString) :
            defaultPalette;

        this.changeAppUiColorTheme(activePaletteData);

        this._vanillaReader = await this.createVanillaReaderModule(
            this._licenseKey,
            this._initialOptions.customWebSpeechTtsSynthesizer
        );

        this._vanillaReaderAPI = VanillaReaderAPI.create(this);


        if (this._onVanillaReaderInstanceCreatedCallback) {
            this._onVanillaReaderInstanceCreatedCallback();
        }

        this._vanillaReaderUi = await this.createVanillaReaderUiModule(
            this._applicationViewElement,
            activePaletteData,
            this._vanillaReaderAPI
        );

        // Now that the UI has rendered, we can get the containing element for the VanillaReaderView.
        this._vanillaReader.setReaderViewElement(this._vanillaReaderUi.vanillaReaderViewElement);

        if (activePaletteData) {
            this._vanillaReaderUi.setUiColorTheme(activePaletteData);
        }

        if(this._onVanillaReaderUiInstanceCreatedCallback) {
            this._onVanillaReaderUiInstanceCreatedCallback();
        }

        this._checkNetworkState();
        this._registerModulesOnWindowObject();

        MasterPlanPostMessageHandler.create(this);

        if (this._onAppLoadedCallback) {
            this._onAppLoadedCallback();
        }
    }

    onVanillaReaderInstanceCreated(callback: VanillaVoidCallback): void {
        this._onVanillaReaderInstanceCreatedCallback = callback;
    }

    onVanillaReaderUiInstanceCreated(callback: VanillaVoidCallback): void {
        this._onVanillaReaderUiInstanceCreatedCallback = callback;
    }

    onAppLoaded(callback: VanillaVoidCallback): void {
        this._onAppLoadedCallback = callback;
    }

    getVanillaReaderInstance(): VanillaReader | undefined {
        return this._vanillaReader;
    }

    getVanillaReaderUiInstance(): VanillaReaderUI | undefined {
        return this._vanillaReaderUi;
    }

    getVanillaReaderBookshelfInstance(): VanillaReaderBookshelf | undefined {
        return this._vanillaReaderBookshelf;
    }

    getVanillaReaderApiInstance(): VanillaReaderAPI | undefined {
        return this._vanillaReaderAPI;
    }

    async createVanillaReaderModule(licenseApiKey: string, customWebSpeechTtsSynthesizer?: IVanillaTtsServiceProvider): Promise<VanillaReader> {
        // The user profile data holds the unique user identifier that is used as token when loading publications.
        const userProfileData: IVanillaReaderUserProfileData = await VanillaReaderAppUserProfile.fetchOrCreateUserProfile();

        // Get any previously saved framework options from local storage. This will later be used to initialize the
        // VanillaReader to its previous state.
        const storedVanillaReaderOptionsData = await this._vanillaReaderAppStorage?.optionsDataStore.fetchVanillaReaderOptionsData();

        const vanillaReaderViewOptions = storedVanillaReaderOptionsData?.viewOptions;
        let vanillaSyncMediaOptions: IVanillaReaderSyncMediaOptions | undefined = storedVanillaReaderOptionsData?.syncMediaOptions;

        if(customWebSpeechTtsSynthesizer) {
            if(!vanillaSyncMediaOptions) {
                vanillaSyncMediaOptions = {
                    webSpeechTtsSynthesizer: customWebSpeechTtsSynthesizer
                }
            }
        }

        if (vanillaReaderViewOptions) {
            if (this._initialOptions.useReducedMotion) {
                vanillaReaderViewOptions.useReducedMotion = true;
            }
        }

        // Needed to add a ts-ignore as the TS definitions do not recognize the `downlink` property :/
        // @ts-ignore
        const slowNetwork: boolean = navigator.connection?.downlink < 1.5 && !this._initialOptions.forceHighSpeedConnection;

        const vanillaReader = new VanillaReader(undefined, licenseApiKey, userProfileData, {
            slowNetwork,
            storeOnDevice: this._initialOptions.useDeviceStorage,
            viewOptions: vanillaReaderViewOptions,
            syncMediaOptions: vanillaSyncMediaOptions,
            customReaderViewContentOnLoading: '<div class="vanilla-reader__loader"><div class="vanilla-reader__loader__logo"></div><div class="vanilla-reader__loader__spinner"></div></div>',
            customReaderViewContentOnLoadError: '<div id="vanilla-reader__reader-view__load-error">💥</div>',
        }, this._vanillaReaderAppStorage.optionsDataStore,
        );

        // Add event listeners for connection related events. This check is unfortunately not enough as it only reports if
        // there is an active connection, not that a connection can reach the Internet. In addition to these events we also
        // set up a reoccurring fetch request in the `checkNetworkState()` function.
        window.addEventListener('online', () => {
            vanillaReader.isOffline = false;
        });

        window.addEventListener('offline', () => {
            vanillaReader.isOffline = true;
        });

        window.addEventListener('resize', () => {
            vanillaReader.refreshReaderView();
        });

        return vanillaReader;
    }

    async createVanillaReaderUiModule(
        readerViewElement: HTMLElement,
        palette: IVanillaReaderUiColorTheme,
        vanillaReaderAPI: VanillaReaderAPI
    ): Promise<VanillaReaderUI> {
        let vanillaReaderUi = new VanillaReaderUI(readerViewElement, this._vanillaReaderBookshelf, this._vanillaReaderAppStorage.optionsDataStore, this._initialOptions.useDeviceStorage, palette);

        // When the user wants to open a new book we reload the index.html page to start from a clean state.
        // We take care to keep the `pwa=true` query parameter on reload.
        vanillaReaderAPI.onPublicationClose((_eventData) => {
            this.closePublication();
        });

        // Event fired when the VanillaReaderUI has updated settings. These are saved to the store in the `saveVanillaReaderOptionsState`
        // function.
        vanillaReaderAPI.onAppUiOptionsChanged((eventData)=>{
            this._vanillaReaderAppStorage.saveVanillaReaderUiOptions(eventData.detail.uiOptionsData).catch(console.warn);
        })

        vanillaReaderAPI.onAppUiOptionsPresetApplied((eventData)=> {
            this._vanillaReaderAppStorage.saveVanillaReaderUiOptions(eventData.detail.uiOptions).catch(console.warn);
        })

        return vanillaReaderUi;
    }

    async loadPublicationFromFile(
        fileObject: File,
        initialLocator: string | undefined,
        useDeviceStorage: boolean,
    ): Promise<void> {
        let shelfItem = await this._vanillaReaderBookshelf.fetchShelfItemByFileName(fileObject.name);

        if (!shelfItem) {
            shelfItem = await this._vanillaReaderBookshelf.addShelfItem(
                fileObject.name,
                `file://${fileObject.name}`,
            );
        }

        initialLocator = initialLocator || shelfItem.readingPositionData?.locator;

        this._vanillaReader?.loadPublicationFromFile(fileObject, shelfItem, initialLocator, useDeviceStorage).catch(console.warn);
    };

    async loadPublicationFromUrl(
        url: string,
        initialLocator?: string | undefined,
        useDeviceStorage: boolean = this._initialOptions.useDeviceStorage
    ): Promise<void> {
        let fileName = url.slice(url.lastIndexOf('/') + 1);

        let shelfItem = await this._vanillaReaderBookshelf.fetchShelfItemByFileName(fileName);

        if (!shelfItem) {
            shelfItem = await this._vanillaReaderBookshelf.addShelfItem(
                fileName,
                url,
            );
        }

        initialLocator = initialLocator || shelfItem.readingPositionData?.locator;

        // let's see if we have any publication data stored for this publication file name.
        await this._vanillaReader?.loadPublicationFromUrl(url, shelfItem, undefined, initialLocator, useDeviceStorage);
        this._vanillaReaderUi?.toaster.toast(`Opening ${fileName}`).catch(console.warn);
        this._vanillaReaderUi?.dialogOpenFile.close(true);
    }

    async loadPublicationFromAws( 
        base64: string,
        initialLocator?: string | undefined,
        useDeviceStorage: boolean = this._initialOptions.useDeviceStorage
    ): Promise<void> {
        let url = 'https://rh771cx8n6.execute-api.eu-west-2.amazonaws.com/masterplan-reader/catalog/publication/' + base64;
        const unencodedUrl = atob(base64);
        const fileName = unencodedUrl.substring(unencodedUrl.lastIndexOf("/") + 1);
        let shelfItem = await this._vanillaReaderBookshelf.fetchShelfItemByFileName(fileName);

        if (!shelfItem) {
            shelfItem = await this._vanillaReaderBookshelf.addShelfItem(
                fileName,
                url,
            );
        }
        initialLocator = initialLocator || shelfItem.readingPositionData?.locator;
        await this._vanillaReader?.loadPublicationFromAwsLambda(url, () => {
            return this.fetchAwsS3SignedPublicationUrl(base64);
        }, shelfItem, undefined, initialLocator, useDeviceStorage);
        this._vanillaReaderUi?.masterplanStart.close(true);
    }

    async fetchAwsS3SignedPublicationUrl(uid: string): Promise<string> {
        return fetch('https://rh771cx8n6.execute-api.eu-west-2.amazonaws.com/masterplan-reader/catalog/publication/' + uid + '?invoke=getSignedUrl').then(async (response) => {
            const data = await response.json();
            return data.url;
        });
    }

    async loadPublicationFromShelfItemData(shelfItemData: IVanillaReaderShelfItemData): Promise<void> {
        let shelfItem = await this._vanillaReaderBookshelf.fetchShelfItemByFileName(shelfItemData.fileName);

        if (!shelfItem) {
            console.warn(`VanillaReaderAppMain.loadPublicationFromShelfItemData(), Unable to load "${shelfItemData.fileName}".`);
            return;
        }

        if (await shelfItem.hasStoredResource()) {
            this._vanillaReader?.loadPublicationFromStore(shelfItem.fileName, shelfItem.fileSourceUri, shelfItem, shelfItem.readingPositionData?.locator);
        } else {
            console.warn(`VanillaReaderAppMain.loadPublicationFromShelfItemData(), Item "${shelfItemData.fileName}" has no stored resource.`)
        }

    }

    async deletePublicationResources(shelfItemData: IVanillaReaderShelfItemData): Promise<void> {
        await this._vanillaReaderBookshelf.deleteShelfItem(shelfItemData);
    }

    /*
    *
    * This method changes the color theme for the application UI based on a `IVanillaReaderUiColorTheme`.
    * The palettes are defined in the `VanillaReaderAppUiDefaults` class.
    *
    */
    changeAppUiColorTheme(palette: IVanillaReaderUiColorTheme | undefined): void {
        // Normally the `!` operator is a big no-no. In this case though we know that the `VanillaReaderAppUiDefaults`
        // has a set value for all it's members.
        let defaultPalette: IVanillaReaderUiColorTheme = VanillaReaderAppAssets.ColorThemes.find(palette => palette.name === 'Dark')!;

        if (palette) {
            document.documentElement.style.backgroundColor = palette.backgroundDark;
        } else {
            document.documentElement.style.backgroundColor = defaultPalette.backgroundLight;
        }

        if (!palette) {
            palette = defaultPalette;
        }

        // Update the VanillaReaderUI theme with the new values
        let uiStyle = this._vanillaReaderUi?.vanillaReaderUIElement.style;
        if(uiStyle) {
            !palette.backgroundDark || uiStyle.setProperty('--vanilla-reader__color__reader__background-dark', palette.backgroundDark);
            !palette.backgroundLight || uiStyle.setProperty('--vanilla-reader__color__reader__background-light', palette.backgroundLight);
            !palette.foregroundDark || uiStyle.setProperty('--vanilla-reader__color__reader__foreground-dark', palette.foregroundDark);
            !palette.foregroundLight || uiStyle.setProperty('--vanilla-reader__color__reader__foreground-light', palette.foregroundLight);
            !palette.uiBackgroundDark || uiStyle.setProperty('--vanilla-reader__color__ui__background-dark', palette.uiBackgroundDark);
            !palette.uiBackgroundLight || uiStyle.setProperty('--vanilla-reader__color__ui__background-light', palette.uiBackgroundLight);
            !palette.uiForegroundDark || uiStyle.setProperty('--vanilla-reader__color__ui__foreground-dark', palette.uiForegroundDark);
            !palette.uiForegroundLight || uiStyle.setProperty('--vanilla-reader__color__ui__foreground-light', palette.uiForegroundLight);
            !palette.uiOutlineDark || uiStyle.setProperty('--vanilla-reader__color__ui__outline-dark', palette.uiOutlineDark);
            !palette.uiOutlineLight || uiStyle.setProperty('--vanilla-reader__color__ui__outline-light', palette.uiOutlineLight);
            !palette.accent || uiStyle.setProperty('--vanilla-reader__color__ui__accent', palette.accent);
        }

        this._activeAppUiTheme = palette;


        // We need to save an extra copy of the active palette in local storage so that we can retrieve it synchronously
        // at app start.
        localStorage.setItem(VanillaReaderLocalStorageKeys.VanillaReaderActiveColorPalette, JSON.stringify(palette));

        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('APP_UI_THEME_CHANGED', {colorPalette: palette}));

    };

    async performDataMigrationFromLocalStorage(): Promise<number> {
        return this._vanillaReaderAppStorage.performDataMigrationFromLocalStorage();
    }

    getActiveUiPresetData(): VanillaReaderUiActiveOptionsPresetData | undefined {
        if(!this._vanillaReaderAppStorage) {
            return;
        }
        return this._vanillaReaderAppStorage.getActiveVanillaReaderUiOptionsPresetData();
    }

    setActiveUiPresetData(optionsPresetData: VanillaReaderUiActiveOptionsPresetData): void {
        if(!this._vanillaReaderAppStorage) {
            return;
        }
        this._vanillaReaderAppStorage.setActiveVanillaReaderUiOptionsPresetData(optionsPresetData);
    }

    clearActiveUiPresetData(): void {
        if(!this._vanillaReaderAppStorage) {
            return;
        }
        this._vanillaReaderAppStorage.clearActiveVanillaReaderUiOptionsPresetData();
    }

    closePublication(): void {
        if (this._vanillaReader && this._vanillaReaderUi) {
            let readerOptionsData = this._vanillaReader.serializeToOptionsData();
            let uiOptionsData = this._vanillaReaderUi.serializeToUiOptionsData();
            this._vanillaReaderAppStorage.saveVanillaReaderAppOptionsState(readerOptionsData, uiOptionsData).then(() => {
                window.location.href = `index.html${this._initialOptions.useDeviceStorage ? '?pwa=true' : ''}`;
            }).catch(console.error);
        }
    }



    /**
     *
     * PRIVATE METHODS
     *
     * */

    private static _onBookshelfUpdated(updatedShelfItems: VanillaReaderShelfItem[]): void {
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('PUBLICATION_SHELF_UPDATED', {
            shelfItems: updatedShelfItems,
        }));
    }

    private static _onBookshelfItemDeleted(): void {
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('PUBLICATION_RESOURCES_DELETED', {}));
    }

    /*
    *
    * This function simply pings an HTML page on our server to see if we are still connected to the network.
    * This is still the only real way to check connectivity since the existing browser APIs are very hard to work with
    * reliably.
    *
    * When we get a timeout we set the `VanillaReader.isOffline` property to communicate to the application that it should
    * change its internal behaviour to adapt.
    *
     */
    _checkNetworkState(): void {
        try {
            fetch('https://demo.colibrio.com/ping.html', {
                mode: 'no-cors',
                method: 'HEAD',
            }).then((res: Response) => {
                // Any status less than 500 means that we got something back from the server.
                if (res.status < 500) {
                    this._isOffline = false;
                }
            }).catch((_err) => {
                this._isOffline = true;
            });
        } catch {
            this._isOffline = true;
        }
        window.setTimeout(this._checkNetworkState, 2500);
    }

    /**
     *
     * BROWSER CONSOLE HELPERS
     *
     * Let's export the VanillaReader and the VanillaReaderUI so that we can play around with the APIs
     * more easily. The VanillaReader is meant to be a fun playground! 😊
     *
     * */
    _registerModulesOnWindowObject(): void {
        (window as any).vanillaReaderAppMain = this;
        (window as any).vanillaReader = this._vanillaReader;
        (window as any).vanillaReaderAPI = this._vanillaReader ? VanillaReaderAPI.instance : undefined;
        (window as any).vanillaReaderUi = this._vanillaReaderUi;
        (window as any).vanillaReaderBookshelf = this._vanillaReaderBookshelf;
        (window as any).VanillaReaderEventBus = VanillaReaderEventBus;
        (window as any).EpubCfiParser = EpubCfiParser;
        (window as any).TreeNodeWalker = TreeNodeWalker;
        (window as any).ObjectUtils = ObjectUtils;

        // Two very handy utility classes for working with range data. Also classes good to have access to in the console.
        (window as any)['IntRange'] = IntRange;
        (window as any)['IntSequence'] = IntSequence;

    }

}
